/**
 * RealtimeClient - Browser client for OpenAI Realtime API (via relay server)
 * Replaces @openai/realtime-api-beta for GA API compatibility
 */

type EventHandler = (...args: any[]) => void;

const generateId = (prefix = 'evt_') =>
  `${prefix}${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

// Convert audio data to base64
const toBase64 = (data: Int16Array | ArrayBuffer): string => {
  const buffer = data instanceof ArrayBuffer ? data : data.buffer;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export interface ItemType {
  id?: string;
  type?: string;
  role?: string;
  status?: string;
  content?: any[];
  formatted?: {
    text?: string;
    transcript?: string;
    audio?: Int16Array;
    output?: string;
    tool?: { name?: string; arguments?: string };
    file?: { url?: string };
  };
  [key: string]: any;
}

export interface SessionResourceType {
  type?: string;
  instructions?: string;
  voice?: string;
  input_audio_transcription?: { model?: string };
  turn_detection?: { type?: string } | null;
  tools?: any[];
  [key: string]: any;
}

class ConversationState {
  items: ItemType[] = [];

  getItems(): ItemType[] {
    return this.items;
  }

  clear() {
    this.items = [];
  }

  upsert(item: ItemType) {
    if (!item?.id) return;
    const idx = this.items.findIndex((i) => i.id === item.id);
    if (idx >= 0) {
      this.items[idx] = { ...this.items[idx], ...item };
    } else {
      this.items.push(item);
    }
  }

  remove(id: string) {
    this.items = this.items.filter((i) => i.id !== id);
  }
}

class RealtimeTransport {
  ws: WebSocket | null = null;
  // [DIAG idle-timing] Wall-clock ms of the last outbound frame on this socket. Used to
  // compute "seconds since last send" in close/heartbeat logs so we can tell an idle
  // Heroku H15 reap (sinceLastSend ≈ 55s) apart from an early/double-client death.
  lastSendAt: number = 0;
  private handlers: Map<string, Set<EventHandler>> = new Map();

  on(event: string, handler: EventHandler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
  }

  off(event: string, handler: EventHandler) {
    this.handlers.get(event)?.delete(handler);
  }

  emit(event: string, ...args: any[]) {
    this.handlers.get(event)?.forEach((h) => h(...args));
    // Wildcard support
    if (event.startsWith('server.')) {
      this.handlers.get('server.*')?.forEach((h) => h(...args));
    }
  }

  send(eventName: string, data: any = {}) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    const event = {
      event_id: data.event_id || generateId(),
      type: eventName,
      ...data,
    };
    this.ws.send(JSON.stringify(event));
    this.lastSendAt = Date.now();
  }
}

interface RealtimeClientOptions {
  url?: string;
  apiKey?: string;
  dangerouslyAllowAPIKeyInBrowser?: boolean;
}

// [DIAG double-client] Monotonic instance counter — every creation is logged with its
// call site so ref-overwrite races can be identified from production console output.
let _clientInstanceCounter = 0;

export class RealtimeClient {
  private url: string;
  private apiKey?: string;
  private handlers: Map<string, Set<EventHandler>> = new Map();
  private sessionConfig: SessionResourceType = {};
  private turnDetectionType: string | null = null;

  // [DIAG double-client] Serial number of this instance (#1, #2, ...) — compare against
  // the serial seen in [sendTextQuery] warnings to spot a replaced clientRef.
  readonly _serial: number;

  // [DIAG idle-timing] Wall-clock ms when this socket's onopen fired. Used to report how
  // long the socket lived before it closed.
  private _openedAt: number = 0;

  realtime: RealtimeTransport;
  conversation: ConversationState;

  constructor({ url, apiKey, dangerouslyAllowAPIKeyInBrowser }: RealtimeClientOptions = {}) {
    this._serial = ++_clientInstanceCounter;
    // Stack line 3 = the creation call site (constructor → caller).
    const _site = (new Error().stack || '').split('\n')[2]?.trim() || 'unknown';
    console.log(`[RealtimeClient] CREATED #${this._serial} at ${new Date().toISOString()} | site=${_site}`);
    // For direct OpenAI connection (no relay), build URL with API key
    if (apiKey && !url) {
      if (!dangerouslyAllowAPIKeyInBrowser) {
        console.warn('[RealtimeClient] Using API key in browser without dangerouslyAllowAPIKeyInBrowser flag');
      }
      this.url = `wss://api.openai.com/v1/realtime?model=gpt-realtime-2.1-mini`;
      this.apiKey = apiKey;
    } else {
      this.url = url || '';
    }
    this.realtime = new RealtimeTransport();
    this.conversation = new ConversationState();
  }

  isConnected(): boolean {
    return this.realtime.ws !== null && this.realtime.ws.readyState === WebSocket.OPEN;
  }

  async connect(): Promise<boolean> {
    if (this.isConnected()) {
      throw new Error('Already connected');
    }
    // Fresh attempt — clear the prior open marker so onerror can tell "never opened this
    // time" from "opened on a previous connect()" (relevant on reconnect of the same client).
    this._openedAt = 0;

    return new Promise((resolve, reject) => {
      // For direct OpenAI connection, use subprotocols to pass API key
      const ws = this.apiKey
        ? new WebSocket(this.url, [
            'realtime',
            `openai-insecure-api-key.${this.apiKey}`,
          ])
        : new WebSocket(this.url);

      // [FIX double-client orphan] Expose the socket SYNCHRONOUSLY, while it is still
      // CONNECTING. Previously realtime.ws was assigned only in onopen, so for the entire
      // connect handshake (often 200ms+) clientRef.current.realtime.ws was null — and the
      // isClientWsLive() "is a socket already live (OPEN *or CONNECTING*)?" guard therefore
      // returned false. A second init/connectConversation pass then spawned a duplicate
      // client and overwrote clientRef, orphaning THIS connecting socket: the greeting plays
      // on the orphan (its listeners are attached), but the mic/commit hits the unconnected
      // replacement ("isConnected=false wsState=undefined" on the first stopRecording).
      // isConnected() still gates on readyState===OPEN, so nothing treats CONNECTING as ready.
      this.realtime.ws = ws;

      ws.onopen = () => {
        this._openedAt = Date.now();
        this.realtime.lastSendAt = Date.now();
        console.log(
          `[RealtimeClient] #${this._serial} WS OPEN at ${new Date().toISOString()} | url=${this.url}`
        );
        resolve(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this._handleServerEvent(data);
        } catch (err) {
          console.error('[RealtimeClient] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        const openFor = this._openedAt
          ? ((Date.now() - this._openedAt) / 1000).toFixed(1)
          : 'n/a';
        const sinceLastSend = this.realtime.lastSendAt
          ? ((Date.now() - this.realtime.lastSendAt) / 1000).toFixed(1)
          : 'n/a';
        // sinceLastSend ≈ 55s alongside code 1006 = Heroku H15 idle reap (no keepalive).
        // A small sinceLastSend with code 1006 points at a network/double-client death instead.
        console.warn(
          `[RealtimeClient] #${this._serial} WS CLOSE at ${new Date().toISOString()} | ` +
          `code=${event.code} reason="${event.reason}" | openFor=${openFor}s sinceLastSend=${sinceLastSend}s`
        );
        this.realtime.ws = null;
        this._emit('disconnected');
        this.realtime.emit('close', { code: event.code, reason: event.reason });
      };

      ws.onerror = (err) => {
        console.error('[RealtimeClient] WebSocket error:', err);
        // Reject the connect() promise only if the socket never finished opening. (Now that
        // realtime.ws is assigned synchronously above, "!this.realtime.ws" can no longer stand
        // in for "never opened" — use _openedAt, which is set in onopen.)
        if (!this._openedAt) {
          reject(new Error('Connection failed'));
        }
        this._emit('error', err);
      };
    });
  }

  disconnect() {
    if (this.realtime.ws) {
      this.realtime.ws.close(1000, 'Client disconnected');
      this.realtime.ws = null;
    }
  }

  // Event handling
  on(event: string, handler: EventHandler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
  }

  off(event: string, handler: EventHandler) {
    this.handlers.get(event)?.delete(handler);
  }

  private _emit(event: string, ...args: any[]) {
    this.handlers.get(event)?.forEach((h) => h(...args));
  }

  private _handleServerEvent(event: any) {
    // Emit raw event for listeners
    this._emit('realtime.event', { time: new Date().toISOString(), event });
    this.realtime.emit(`server.${event.type}`, event);

    // Update conversation state (GA: conversation.item.added/done, beta: conversation.item.created)
    if (event.type === 'conversation.item.created' ||
        event.type === 'conversation.item.added' ||
        event.type === 'conversation.item.done') {
      console.log(`[RealtimeClient] ${event.type}: role=${event.item?.role}, id=${event.item?.id}, content=${JSON.stringify(event.item?.content?.[0])?.slice(0, 100)}`);
      this.conversation.upsert(event.item);
      this._emit('conversation.updated', { item: event.item, delta: null });
    } else if (event.type === 'conversation.item.deleted') {
      this.conversation.remove(event.item_id);
    } else if (event.type === 'response.output_item.done') {
      this.conversation.upsert(event.item);
      this._emit('conversation.updated', { item: event.item, delta: null });
    } else if (event.type === 'response.audio_transcript.done' ||
               event.type === 'response.output_audio_transcript.done') {
      // Update item with assistant transcript
      const item = this.conversation.items.find((i) => i.id === event.item_id);
      if (item) {
        item.formatted = item.formatted || {};
        item.formatted.transcript = event.transcript;
        this._emit('conversation.updated', { item, delta: null });
      }
    } else if (event.type === 'conversation.item.input_audio_transcription.completed') {
      // Update item with user speech transcript
      const item = this.conversation.items.find((i) => i.id === event.item_id);
      if (item) {
        item.formatted = item.formatted || {};
        item.formatted.transcript = event.transcript;
        this._emit('conversation.updated', { item, delta: null });
      }
    } else if (event.type === 'response.audio_transcript.delta' ||
               event.type === 'response.output_audio_transcript.delta') {
      // Progressive transcript delta - build up transcript as it streams
      const item = this.conversation.items.find((i) => i.id === event.item_id);
      if (item) {
        item.formatted = item.formatted || {};
        item.formatted.transcript = (item.formatted.transcript || '') + event.delta;
        this._emit('conversation.updated', { item, delta: { transcript: event.delta } });
      }
    } else if (event.type === 'response.audio.delta' ||
               event.type === 'response.output_audio.delta') {
      // Audio delta
      const item = this.conversation.items.find((i) => i.id === event.item_id);
      this._emit('conversation.updated', { item, delta: { audio: event.delta } });
    } else if (event.type === 'session.updated') {
      this.sessionConfig = event.session || {};
      this.turnDetectionType = this.sessionConfig.turn_detection?.type || null;
    } else if (event.type === 'error') {
      this._emit('error', event.error);
    }
  }

  // High-level API methods
  sendUserMessageContent(content: Array<{ type: string; text?: string; audio?: string }>) {
    const event = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content,
      },
    };
    this.realtime.send('conversation.item.create', event);
  }

  appendInputAudio(audio: Int16Array | ArrayBuffer) {
    const base64 = toBase64(audio);
    this.realtime.send('input_audio_buffer.append', { audio: base64 });
  }

  commitInputAudio() {
    this.realtime.send('input_audio_buffer.commit', {});
  }

  createResponse() {
    this.realtime.send('response.create', {});
  }

  cancelResponse(id?: string, sampleCount?: number) {
    this.realtime.send('response.cancel', {});
    if (id) {
      this._emit('cancelResponse', id, sampleCount);
    }
  }

  updateSession(config: SessionResourceType) {
    // Ensure GA session type is set
    const session = { type: 'realtime', ...config };
    this.realtime.send('session.update', { session });
  }

  deleteItem(id: string) {
    this.realtime.send('conversation.item.delete', { item_id: id });
    this.conversation.remove(id);
  }

  getTurnDetectionType(): string | null {
    return this.turnDetectionType;
  }

  addTool(definition: any, handler: (args: any) => Promise<any>) {
    // Store tool handler for later use
    // The relay server handles actual tool execution
    console.log('[RealtimeClient] Tool registered:', definition.name);
  }

  reset() {
    this.conversation.clear();
    this.sessionConfig = {};
    this.turnDetectionType = null;
  }
}

export default RealtimeClient;
